import React, { Dispatch, MutableRefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Account } from '@Types/account/Account';
import { Category } from '@Types/product/Category';
import AccountButton from 'components/commercetools-ui/header/account-button';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference, ReferenceLink } from 'helpers/reference';
import { MediaItemWithMods, useAccount, useCart, useWishlist } from 'frontastic';
import { localizedProductCategories } from 'frontastic/actions/productCategories';
import Image from 'frontastic/lib/image';
import { FullPageWidthWrapper } from '../full-bleed';
import { useSecretFeature } from 'helpers/hooks/useSecretFeature';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { useLockedBody } from 'helpers/hooks/useLockedBody';
import { useMutationObserver } from 'helpers/hooks/useMutationObserver';
import RequestBanner from './request-banner';

export interface Link {
  name: string;
  reference: Reference;
}

const useClickOutside = (
  refs: MutableRefObject<HTMLElement>[],
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
) => {
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!refs.some((ref) => ref.current) || !isOpen || refs.some((ref) => ref.current.contains(target))) return;
      setIsOpen(false);
    };

    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [refs, isOpen, setIsOpen]);
};

interface HierarchicalCategory extends Category {
  children?: HierarchicalCategory[];
}

export interface NavbarProps {
  banner?: { enabled: boolean; text?: string; bgColor?: string; color?: string };
  links: Link[];
  cartItemCount: number;
  wishlistItemCount?: number;
  logo: MediaItemWithMods;
  logoLink: Reference;
  account: Account;
  accountLink: Reference;
  wishlistLink?: Reference;
  cartLink: Reference;
  previewId: string;
  categoryHierarchy?: HierarchicalCategory[];
}

const CheckoutElementId = 'commercetools-checkout';

const ECommerceNavbar = (props: NavbarProps) => {
  const router = useRouter();
  // const client = getStrapiGraphQlClient();
  const [open, setOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isWishlist, setIsWishlist] = useState(false);
  const [debug, setDebug] = useState(false);
  const [keys, setKeys] = useState<string>('');
  const featureEnabled = useSecretFeature('secret');
  const [showNavigation, setShowNavigation] = useState<boolean>(true);
  const [, setRef] = useMutationObserver((mutationList, _) => {
    mutationList.forEach((item) => {
      if (
        Array.from(item.addedNodes.values()).some(
          (node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).id === CheckoutElementId,
        )
      ) {
        setShowNavigation(false);
      }
      if (
        Array.from(item.removedNodes.values()).some(
          (node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).id === CheckoutElementId,
        )
      ) {
        setShowNavigation(true);
      }
    });
  });

  const menuTrigger = useRef(null);
  const menuRef = useRef(null);
  const cartTrigger = useRef(null);
  const wishlistTrigger = useRef(null);
  const cartBox = useRef(null);
  const wishlistRef = useRef(null);

  useClickOutside([menuRef, menuTrigger], open, setOpen);
  useClickOutside([cartBox, cartTrigger], isCartOpen, setIsCartOpen);
  useClickOutside([wishlistRef, wishlistTrigger], isWishlist, setIsWishlist);

  const { logout, loggedIn, account } = useAccount();
  const { removeItem, data: cartData } = useCart();
  const { removeLineItem, data: wishListData } = useWishlist();

  const locales = router.locales.filter((locale) => locale !== 'default');

  const onlyUnique = (value: any, index: number, array: any[]) => {
    return array.indexOf(value) === index;
  };

  const selectedRegion = router.locale.split('-')[1];

  const regions = locales.map((locale) => locale.split('-')[1]).filter(onlyUnique);

  const { formatMessage } = useFormat({ name: 'common' });

  const languageMapper: Record<string, string> = {
    en: formatMessage({ id: 'language.en', defaultMessage: 'English' }),
    de: formatMessage({ id: 'language.de', defaultMessage: 'Deutsch' }),
    fr: formatMessage({ id: 'language.fr', defaultMessage: 'Française' }),
  };

  useEffect(() => {
    setOpen(false);
  }, [router.asPath]);

  useEffect(() => {
    const onKey = (e) => {
      const newKeys = keys + e.key;
      if (newKeys.endsWith('debug')) {
        setDebug(true);
      }
      setKeys(newKeys);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [keys, setKeys]);

  const filteredLocales = locales.filter((locale) => locale !== 'default' && locale.endsWith(selectedRegion));

  const clearContext = async () => {
    if (cartData && cartData?.lineItems) {
      for (const lineItem of cartData?.lineItems) {
        await removeItem(lineItem.lineItemId);
      }
    }
    if (wishListData && wishListData?.lineItems) {
      for (const lineItem of wishListData?.lineItems) {
        await removeLineItem(lineItem.lineItemId);
      }
    }
    if (loggedIn) await logout();
  };

  const changeLocale = async (locale: string) => {
    const { newSlug } = await localizedProductCategories(router.asPath.split('?')[0], locale);
    if (newSlug) {
      router.push(newSlug, newSlug, { locale });
    } else if (router.asPath.startsWith('/c/')) {
      router.push(`/c?currentPath=/${router.asPath.slice(1)}&currentLocale=${router.locale}`, null, {
        locale,
      });
    } else {
      router.push(router.asPath, router.asPath, { locale });
    }
  };

  useEffect(() => {
    setRef(document.body);
  }, []);

  const mainButtons = (
    <div className="flex w-full items-center justify-end space-x-4 pr-0">
      <div>
        <ReferenceLink
          target={{
            link: '/search',
            type: 'link',
            openInNewWindow: false,
          }}
          className="text-dark relative hidden h-10 w-10 items-center justify-center rounded-full border-[.5px] border-[#f4f7ff] bg-[#f4f7ff] md:flex"
          data-testid="search-trigger"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M2.99988 7.81696C2.99988 4.60434 5.57707 2 8.75619 2C11.9353 2 14.5125 4.60434 14.5125 7.81696C14.5125 11.0296 11.9353 13.6339 8.75619 13.6339C5.57707 13.6339 2.99988 11.0296 2.99988 7.81696ZM8.75619 3.30069C6.28793 3.30069 4.287 5.32269 4.287 7.81696C4.287 10.3112 6.28793 12.3332 8.75619 12.3332C11.2245 12.3332 13.2254 10.3112 13.2254 7.81696C13.2254 5.32269 11.2245 3.30069 8.75619 3.30069Z"
              fill="#242424"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.9896 11.9197C12.2715 11.701 12.6755 11.7545 12.892 12.0394L15.8667 15.9535C16.0832 16.2384 16.0302 16.6466 15.7483 16.8654C15.4664 17.0842 15.0624 17.0306 14.8459 16.7457L11.8712 12.8316C11.6547 12.5468 11.7077 12.1385 11.9896 11.9197Z"
              fill="#242424"
            />
          </svg>
        </ReferenceLink>
      </div>

      <div>
        <div className="text-dark relative hidden h-10 w-10 items-center justify-center rounded-full border-[.5px] border-[#f4f7ff] bg-[#f4f7ff] md:flex">
          <AccountButton
            account={props.account}
            accountLink={{
              link: !router.asPath.includes('current') ? `/login?current${router.asPath}` : router.asPath,
              target: !router.asPath.includes('current') ? `/login?current${router.asPath}` : router.asPath,
              type: 'link',
              openInNewWindow: false,
            }}
            data-testid="account-trigger"
          />
        </div>
      </div>
      <div className="relative z-20">
        <div className="flex max-w-[200px] justify-end">
          <ReferenceLink
            refObject={wishlistTrigger}
            target={props.wishlistLink}
            // onClick={() => setIsWishlist(!isWishlist)}
            className="text-dark full relative flex h-10 w-10 items-center justify-center rounded-full md:border-[.5px] md:border-[#f4f7ff] md:bg-[#f4f7ff]"
            data-testid="wishlist-trigger"
          >
            <div className="hidden md:block">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.00007 15.447C8.72377 15.447 8.42445 15.3528 8.21723 15.1406C7.68767 14.6692 7.1581 14.2213 6.69761 13.8206C5.33916 12.642 4.1649 11.6284 3.33602 10.6148C2.36899 9.43624 1.90849 8.30479 1.90849 7.05548C1.90849 5.82975 2.32294 4.6983 3.08275 3.87329C3.84256 3.02471 4.90169 2.55327 6.05292 2.55327C6.90483 2.55327 7.71069 2.83613 8.40143 3.37828C8.60865 3.54329 8.81587 3.73186 9.00007 3.94401C9.18427 3.73186 9.39149 3.54329 9.59871 3.37828C10.2894 2.83613 11.0723 2.55327 11.9472 2.55327C13.0984 2.55327 14.1346 3.02471 14.9174 3.87329C15.6772 4.6983 16.0916 5.82975 16.0916 7.05548C16.0916 8.30479 15.6311 9.43624 14.6641 10.6148C13.8352 11.6284 12.661 12.642 11.3025 13.8206C10.842 14.2213 10.3125 14.6692 9.7829 15.1406C9.57568 15.3292 9.27636 15.447 9.00007 15.447ZM6.05292 3.35471C5.13194 3.35471 4.28003 3.73186 3.68139 4.41544C3.05972 5.09902 2.73738 6.01832 2.73738 7.03191C2.73738 8.06907 3.1288 9.03551 3.95768 10.0727C4.74052 11.0391 5.89175 12.0291 7.22718 13.1841C7.68767 13.5849 8.21723 14.0327 8.7468 14.5277C8.88495 14.6456 9.11519 14.6456 9.25334 14.5277C9.80593 14.0563 10.3125 13.5849 10.773 13.1841C12.1084 12.0291 13.2596 11.0391 14.0425 10.0727C14.8713 9.03551 15.2628 8.06907 15.2628 7.03191C15.2628 6.01832 14.9174 5.09902 14.3187 4.41544C13.6971 3.73186 12.8682 3.35471 11.9472 3.35471C11.2565 3.35471 10.6348 3.56686 10.1052 4.01472C9.875 4.17972 9.69081 4.39187 9.48358 4.62759C9.36846 4.76902 9.18427 4.86331 9.00007 4.86331C8.81587 4.86331 8.63168 4.76902 8.51655 4.62759C8.33236 4.39187 8.12513 4.17972 7.89489 4.01472C7.36532 3.59043 6.74366 3.35471 6.05292 3.35471Z"
                  fill="#242424"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.5219 3.22698C8.68347 3.35563 8.84647 3.49928 9.00007 3.65804C9.15367 3.49928 9.31667 3.35563 9.47823 3.22698L9.47929 3.22614C10.2025 2.6585 11.027 2.35986 11.9472 2.35986C13.1546 2.35986 14.2416 2.85547 15.0595 3.74215C15.8558 4.6067 16.285 5.78667 16.285 7.05548C16.285 8.35956 15.8019 9.5329 14.8138 10.7373C13.9885 11.7465 12.8292 12.7522 11.508 13.8984L11.4295 13.9665C11.2698 14.1054 11.1028 14.2493 10.9308 14.3974C10.6037 14.679 10.2587 14.976 9.91228 15.2844C9.67041 15.5041 9.32539 15.6405 9.00007 15.6405C8.68669 15.6405 8.33527 15.5349 8.08384 15.2808C7.73881 14.9737 7.3952 14.6779 7.06937 14.3974C6.89739 14.2493 6.73036 14.1055 6.57069 13.9665C6.54446 13.9438 6.51829 13.9211 6.49218 13.8984C5.17094 12.7522 4.01174 11.7466 3.18639 10.7374C2.19822 9.53297 1.71509 8.3596 1.71509 7.05548C1.71509 5.78716 2.14393 4.60772 2.93956 3.74327C3.73636 2.85394 4.84769 2.35986 6.05292 2.35986C6.9495 2.35986 7.79713 2.65811 8.52084 3.22614L8.5219 3.22698ZM3.08275 3.87329C2.32294 4.6983 1.90849 5.82975 1.90849 7.05548C1.90849 8.30479 2.36899 9.43624 3.33602 10.6148C4.15075 11.6111 5.29915 12.6074 6.62817 13.7603L6.69761 13.8206C6.85576 13.9582 7.02206 14.1014 7.19371 14.2492C7.52185 14.5317 7.86954 14.8311 8.21723 15.1406C8.42445 15.3528 8.72377 15.447 9.00007 15.447C9.27636 15.447 9.57568 15.3292 9.7829 15.1406C10.1306 14.8311 10.4783 14.5317 10.8064 14.2492C10.9781 14.1014 11.1444 13.9582 11.3025 13.8206L11.372 13.7603C12.701 12.6074 13.8494 11.6111 14.6641 10.6148C15.6311 9.43624 16.0916 8.30479 16.0916 7.05548C16.0916 5.82975 15.6772 4.6983 14.9174 3.87329C14.1346 3.02471 13.0984 2.55327 11.9472 2.55327C11.0723 2.55327 10.2894 2.83613 9.59871 3.37828C9.43947 3.50508 9.28024 3.64579 9.13145 3.80042C9.08661 3.84702 9.04272 3.89488 9.00007 3.94401C8.95741 3.89488 8.91353 3.84702 8.86869 3.80042C8.7199 3.64579 8.56067 3.50508 8.40143 3.37828C7.71069 2.83613 6.90483 2.55327 6.05292 2.55327C4.90169 2.55327 3.84256 3.02471 3.08275 3.87329ZM3.82447 4.54557C3.2397 5.18858 2.93079 6.05929 2.93079 7.03191C2.93079 8.01345 3.29893 8.93844 4.10835 9.95141C4.86538 10.8859 5.97659 11.8469 7.2884 12.9814L7.3537 13.0379C7.44972 13.1214 7.54953 13.2077 7.65153 13.2959C8.03474 13.6271 8.45513 13.9905 8.87497 14.3828C8.94143 14.4368 9.06285 14.436 9.12781 14.3806C9.45099 14.1049 9.75237 13.8346 10.0407 13.576C10.2481 13.3899 10.4488 13.2098 10.646 13.0382L10.7117 12.9814C12.0236 11.8469 13.1348 10.8858 13.8918 9.95138C14.7012 8.93843 15.0694 8.01344 15.0694 7.03191C15.0694 6.06311 14.7397 5.19039 14.1745 4.54424C13.5885 3.90058 12.8115 3.54812 11.9472 3.54812C11.2992 3.54812 10.7226 3.74594 10.2301 4.1624L10.2242 4.16741L10.2179 4.17193C10.0118 4.31963 9.84412 4.51039 9.63706 4.74594L9.63109 4.75272C9.48188 4.93418 9.24536 5.05671 9.00007 5.05671C8.75341 5.05671 8.51562 4.93281 8.36656 4.74968L8.36413 4.7467C8.18815 4.5215 7.99383 4.32357 7.78223 4.17193L7.77801 4.1689L7.77396 4.16566C7.27632 3.76695 6.69674 3.54812 6.05292 3.54812C5.18591 3.54812 4.38766 3.90253 3.82689 4.54286L3.82447 4.54557ZM9.48358 4.62759L9.49953 4.60945C9.70031 4.38099 9.88092 4.17548 10.1052 4.01472C10.6348 3.56686 11.2565 3.35471 11.9472 3.35471C12.8682 3.35471 13.6971 3.73186 14.3187 4.41544C14.9174 5.09902 15.2628 6.01832 15.2628 7.03191C15.2628 8.06907 14.8713 9.03551 14.0425 10.0727C13.2745 11.0208 12.1519 11.9916 10.8488 13.1186L10.773 13.1841C10.5814 13.3509 10.3818 13.5298 10.1743 13.7159C9.88304 13.9771 9.57604 14.2524 9.25334 14.5277C9.11519 14.6456 8.88495 14.6456 8.7468 14.5277C8.32869 14.1369 7.91059 13.7755 7.52648 13.4434C7.42408 13.3549 7.3241 13.2685 7.22718 13.1841L7.15139 13.1186C5.84821 11.9916 4.72567 11.0208 3.95768 10.0727C3.1288 9.03551 2.73738 8.06907 2.73738 7.03191C2.73738 6.01832 3.05972 5.09902 3.68139 4.41544C4.28003 3.73186 5.13194 3.35471 6.05292 3.35471C6.74366 3.35471 7.36532 3.59043 7.89489 4.01472C8.12513 4.17972 8.33236 4.39187 8.51655 4.62759C8.63168 4.76902 8.81587 4.86331 9.00007 4.86331C9.18427 4.86331 9.36846 4.76902 9.48358 4.62759Z"
                  fill="#242424"
                />
              </svg>
            </div>
            <div className="block md:hidden">
              <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.0002 19.2306C10.6046 19.2306 10.176 19.0956 9.8793 18.7919C9.12106 18.1169 8.36282 17.4756 7.70348 16.9019C5.75842 15.2143 4.0771 13.7631 2.89029 12.3118C1.50568 10.6243 0.846335 9.00424 0.846335 7.21546C0.846335 5.46043 1.43974 3.84041 2.52765 2.65914C3.61557 1.44412 5.13205 0.769111 6.7804 0.769111C8.00018 0.769111 9.15403 1.17412 10.143 1.95038C10.4397 2.18663 10.7364 2.45664 11.0002 2.76039C11.2639 2.45664 11.5606 2.18663 11.8573 1.95038C12.8463 1.17412 13.9672 0.769111 15.22 0.769111C16.8683 0.769111 18.3518 1.44412 19.4727 2.65914C20.5606 3.84041 21.154 5.46043 21.154 7.21546C21.154 9.00424 20.4947 10.6243 19.1101 12.3118C17.9233 13.7631 16.2419 15.2143 14.2969 16.9019C13.6375 17.4756 12.8793 18.1169 12.1211 18.7919C11.8244 19.0619 11.3958 19.2306 11.0002 19.2306ZM6.7804 1.91663C5.46172 1.91663 4.24194 2.45664 3.3848 3.4354C2.49469 4.41417 2.03315 5.73044 2.03315 7.18171C2.03315 8.66673 2.59359 10.0505 3.7804 11.5355C4.90128 12.9193 6.54963 14.3368 8.46172 15.9906C9.12106 16.5644 9.8793 17.2056 10.6375 17.9144C10.8353 18.0831 11.165 18.0831 11.3628 17.9144C12.154 17.2394 12.8793 16.5644 13.5386 15.9906C15.4507 14.3368 17.0991 12.9193 18.22 11.5355C19.4068 10.0505 19.9672 8.66673 19.9672 7.18171C19.9672 5.73044 19.4727 4.41417 18.6156 3.4354C17.7255 2.45664 16.5386 1.91663 15.22 1.91663C14.231 1.91663 13.3408 2.22038 12.5826 2.86164C12.2529 3.0979 11.9892 3.40165 11.6925 3.73916C11.5277 3.94166 11.2639 4.07666 11.0002 4.07666C10.7364 4.07666 10.4727 3.94166 10.3079 3.73916C10.0441 3.40165 9.74743 3.0979 9.41776 2.86164C8.65952 2.25413 7.76941 1.91663 6.7804 1.91663Z"
                  fill="#242424"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.3155 1.73374C10.5469 1.91795 10.7803 2.12362 11.0002 2.35094C11.2201 2.12362 11.4535 1.91795 11.6848 1.73374L11.6863 1.73254C12.7218 0.919781 13.9024 0.492188 15.22 0.492188C16.9487 0.492188 18.5051 1.20181 19.6763 2.47137C20.8163 3.70924 21.431 5.39875 21.431 7.21546C21.431 9.08266 20.7392 10.7627 19.3244 12.4871C18.1427 13.9322 16.4829 15.3721 14.591 17.0133L14.4787 17.1108C14.25 17.3097 14.0109 17.5157 13.7646 17.7277C13.2962 18.1309 12.8023 18.5562 12.3063 18.9977C11.96 19.3123 11.466 19.5076 11.0002 19.5076C10.5515 19.5076 10.0483 19.3564 9.68831 18.9926C9.19429 18.553 8.7023 18.1294 8.23578 17.7277C7.98952 17.5157 7.75037 17.3098 7.52175 17.1108C7.48419 17.0782 7.44672 17.0457 7.40934 17.0133C5.51757 15.3722 3.8578 13.9323 2.67605 12.4873C1.26116 10.7628 0.569412 9.08272 0.569412 7.21546C0.569412 5.39945 1.18344 3.71071 2.32264 2.47297C3.46351 1.19962 5.05473 0.492188 6.7804 0.492188C8.06413 0.492188 9.27779 0.919222 10.314 1.73254L10.3155 1.73374ZM2.52765 2.65914C1.43974 3.84041 0.846335 5.46043 0.846335 7.21546C0.846335 9.00424 1.50568 10.6243 2.89029 12.3118C4.05683 13.7383 5.70114 15.1648 7.60405 16.8156L7.70348 16.9019C7.92992 17.0989 8.16803 17.3039 8.4138 17.5155C8.88364 17.9201 9.38147 18.3487 9.8793 18.7919C10.176 19.0956 10.6046 19.2306 11.0002 19.2306C11.3958 19.2306 11.8244 19.0619 12.1211 18.7919C12.6189 18.3487 13.1167 17.9201 13.5866 17.5155C13.8323 17.3039 14.0704 17.0989 14.2969 16.9019L14.3963 16.8156C16.2992 15.1648 17.9435 13.7383 19.1101 12.3118C20.4947 10.6243 21.154 9.00424 21.154 7.21546C21.154 5.46043 20.5606 3.84041 19.4727 2.65914C18.3518 1.44412 16.8683 0.769111 15.22 0.769111C13.9672 0.769111 12.8463 1.17412 11.8573 1.95038C11.6293 2.13192 11.4013 2.3334 11.1883 2.5548C11.1241 2.62152 11.0613 2.69005 11.0002 2.76039C10.9391 2.69005 10.8763 2.62152 10.8121 2.5548C10.599 2.3334 10.371 2.13192 10.143 1.95038C9.15403 1.17412 8.00018 0.769111 6.7804 0.769111C5.13205 0.769111 3.61557 1.44412 2.52765 2.65914ZM3.58967 3.62172C2.75238 4.5424 2.31007 5.7891 2.31007 7.18171C2.31007 8.58709 2.83719 9.91152 3.99613 11.3619C5.08006 12.6999 6.67111 14.0759 8.54938 15.7003L8.64288 15.7811C8.78036 15.9008 8.92327 16.0243 9.06931 16.1506C9.61801 16.6249 10.2199 17.1452 10.8211 17.7069C10.9162 17.7841 11.0901 17.7831 11.1831 17.7037C11.6458 17.3089 12.0773 16.9219 12.4901 16.5516C12.7872 16.2851 13.0745 16.0274 13.3569 15.7817L13.451 15.7003C15.3293 14.0758 16.9203 12.6998 18.0043 11.3619C19.1632 9.91149 19.6903 8.58708 19.6903 7.18171C19.6903 5.79457 19.2183 4.54498 18.409 3.61982C17.5699 2.69821 16.4574 2.19355 15.22 2.19355C14.2921 2.19355 13.4665 2.4768 12.7614 3.07309L12.7529 3.08026L12.7439 3.08673C12.4488 3.29821 12.2087 3.57135 11.9122 3.90861L11.9037 3.91833C11.6901 4.17814 11.3514 4.35359 11.0002 4.35359C10.647 4.35359 10.3065 4.17618 10.0931 3.91398L10.0896 3.9097C9.83766 3.58725 9.55943 3.30386 9.25646 3.08673L9.25041 3.0824L9.24461 3.07776C8.53209 2.50688 7.70223 2.19355 6.7804 2.19355C5.53901 2.19355 4.39604 2.701 3.59313 3.61784L3.58967 3.62172ZM11.6925 3.73916L11.7153 3.71319C12.0028 3.38607 12.2614 3.09182 12.5826 2.86164C13.3408 2.22038 14.231 1.91663 15.22 1.91663C16.5386 1.91663 17.7255 2.45664 18.6156 3.4354C19.4727 4.41417 19.9672 5.73044 19.9672 7.18171C19.9672 8.66673 19.4068 10.0505 18.22 11.5355C17.1203 12.8931 15.5131 14.2831 13.6472 15.8967L13.5386 15.9906C13.2643 16.2293 12.9786 16.4855 12.6815 16.752C12.2644 17.126 11.8249 17.5202 11.3628 17.9144C11.165 18.0831 10.8353 18.0831 10.6375 17.9144C10.0389 17.3548 9.44024 16.8373 8.89027 16.3619C8.74365 16.2351 8.6005 16.1114 8.46172 15.9906L8.35321 15.8968C6.4873 14.2831 4.88002 12.8931 3.7804 11.5355C2.59359 10.0505 2.03315 8.66673 2.03315 7.18171C2.03315 5.73044 2.49469 4.41417 3.3848 3.4354C4.24194 2.45664 5.46172 1.91663 6.7804 1.91663C7.76941 1.91663 8.65952 2.25413 9.41776 2.86164C9.74743 3.0979 10.0441 3.40165 10.3079 3.73916C10.4727 3.94166 10.7364 4.07666 11.0002 4.07666C11.2639 4.07666 11.5277 3.94166 11.6925 3.73916Z"
                  fill="#242424"
                />
              </svg>
            </div>
            {!!props.wishlistItemCount && (
              <span
                className="absolute top-[4px] right-[1px] h-4 w-4 rounded-full bg-secondary text-center text-[10px] font-semibold text-white md:-top-1 md:-right-1 md:bg-primary"
                data-testid="wishlist-items-count"
              >
                {props.wishlistItemCount}
              </span>
            )}
          </ReferenceLink>
        </div>
      </div>
      <div className="relative z-20">
        <div className="flex max-w-[200px] justify-end">
          <ReferenceLink
            refObject={cartTrigger}
            // onClick={() => setIsCartOpen(!isCartOpen)}
            target={props.cartLink}
            className="text-dark relative flex h-10 w-10 items-center justify-center rounded-full md:border-[.5px] md:border-[#f4f7ff] md:bg-[#f4f7ff]"
            data-testid="cart-trigger"
          >
            <div className="hidden md:block">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15.9863 13.7554L14.8002 3.78182C14.6816 2.76364 13.804 2 12.7365 2H5.26435C4.22062 2 3.34294 2.76364 3.20061 3.78182L2.01456 13.7554C1.94339 14.3339 2.13316 14.8893 2.53642 15.3289C2.93968 15.7686 3.48527 16 4.07829 16H13.9226C14.5156 16 15.0849 15.7455 15.4644 15.3289C15.844 14.9124 16.0575 14.3339 15.9863 13.7554ZM14.6816 14.6347C14.4919 14.843 14.2309 14.9587 13.9463 14.9587H4.07829C3.79364 14.9587 3.53271 14.843 3.34294 14.6347C3.15317 14.4264 3.05829 14.1488 3.10573 13.8711L4.26806 3.92066C4.3155 3.43471 4.74248 3.06446 5.26435 3.06446H12.7602C13.2584 3.06446 13.6853 3.43471 13.7565 3.92066L14.9426 13.8942C14.9663 14.1719 14.8714 14.4264 14.6816 14.6347Z"
                  fill="#242424"
                />
                <path
                  d="M11.3133 5.1704C11.0286 5.23982 10.8389 5.51751 10.91 5.7952C10.9337 5.93404 10.9575 6.07288 10.9575 6.18858C10.9575 7.22991 10.0798 8.0861 9.01234 8.0861C7.94489 8.0861 7.06721 7.22991 7.06721 6.18858C7.06721 6.04974 7.09093 5.93404 7.11465 5.7952C7.18581 5.51751 6.99604 5.23982 6.71139 5.1704C6.42674 5.10098 6.14208 5.2861 6.07092 5.56379C6.02348 5.77205 5.99976 5.98032 5.99976 6.18858C5.99976 7.80842 7.35186 9.12743 9.01234 9.12743C10.6728 9.12743 12.0249 7.80842 12.0249 6.18858C12.0249 5.98032 12.0012 5.77205 11.9538 5.56379C11.8826 5.2861 11.5979 5.12412 11.3133 5.1704Z"
                  fill="#242424"
                />
              </svg>
            </div>
            <div className="block md:hidden">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21.9806 18.7934L20.2862 4.54545C20.1168 3.09091 18.863 2 17.338 2H6.66353C5.17249 2 3.91866 3.09091 3.71534 4.54545L2.02097 18.7934C1.91931 19.6198 2.19041 20.4132 2.76649 21.0413C3.34257 21.6694 4.12198 22 4.96917 22H19.0324C19.8796 22 20.6929 21.6364 21.2351 21.0413C21.7773 20.4463 22.0823 19.6198 21.9806 18.7934ZM20.1168 20.0496C19.8457 20.3471 19.4729 20.5124 19.0663 20.5124H4.96917C4.56252 20.5124 4.18976 20.3471 3.91866 20.0496C3.64756 19.7521 3.51201 19.3554 3.57979 18.9587L5.24026 4.7438C5.30804 4.04959 5.91801 3.52066 6.66353 3.52066H17.3719C18.0836 3.52066 18.6935 4.04959 18.7952 4.7438L20.4896 18.9917C20.5234 19.3884 20.3879 19.7521 20.1168 20.0496Z"
                  fill="black"
                />
                <path
                  d="M15.3048 6.52873C14.8982 6.6279 14.6271 7.02459 14.7288 7.42129C14.7626 7.61964 14.7965 7.81798 14.7965 7.98327C14.7965 9.47088 13.5427 10.694 12.0178 10.694C10.4928 10.694 9.23901 9.47088 9.23901 7.98327C9.23901 7.78492 9.2729 7.61964 9.30678 7.42129C9.40845 7.02459 9.13735 6.6279 8.7307 6.52873C8.32405 6.42955 7.91741 6.69402 7.81574 7.09071C7.74797 7.38823 7.71408 7.68575 7.71408 7.98327C7.71408 10.2973 9.64566 12.1816 12.0178 12.1816C14.3899 12.1816 16.3215 10.2973 16.3215 7.98327C16.3215 7.68575 16.2876 7.38823 16.2198 7.09071C16.1181 6.69402 15.7115 6.46261 15.3048 6.52873Z"
                  fill="black"
                />
              </svg>
            </div>
            {!!props.cartItemCount && (
              <span
                className="absolute top-[4px] right-[1px] h-4 w-4 rounded-full bg-secondary text-center text-[10px] font-semibold text-white md:-top-1 md:-right-1 md:bg-primary"
                data-testid="cart-items-count"
              >
                {props.cartItemCount}
              </span>
            )}
          </ReferenceLink>
        </div>
      </div>
    </div>
  );

  if (!showNavigation) return <></>;

  return (
    <>
      {/* <!-- ====== Navbar Section Start --> */}
      <FullPageWidthWrapper>
        {props?.banner && props?.banner?.enabled === true && (
          <div
            className="flex h-8 w-full flex-col items-center justify-center py-2"
            style={{ backgroundColor: props?.banner?.bgColor }}
          >
            <div className="w-fit text-sm font-medium" style={{ color: props?.banner?.color }}>
              {props?.banner?.text}
            </div>
          </div>
        )}
        <RequestBanner />
        <header className="w-full bg-white">
          <div className="mx-auto w-full px-4 sm:px-10 lg:max-w-[1280px] lg:px-4">
            <div className="hidden border-b border-[#e7e7e7] sm:block">
              <div className="hidden flex-wrap md:flex">
                <div className="w-full md:w-2/3 lg:w-1/2">
                  <ul className="flex items-center">
                    {props.links?.map((link) => (
                      <li key={link.name} className="py-4 pr-3 pl-3 text-gray-100 first:pl-0">
                        <ReferenceLink
                          target={link.reference}
                          className="inline-block text-xs font-normal text-[#808080] hover:text-primary"
                        >
                          {link.name}
                        </ReferenceLink>
                      </li>
                    ))}

                    {featureEnabled && <li>Secret Feature</li>}
                  </ul>
                  {debug && (
                    <div>
                      <p>cartId: {cartData?.cartId}</p>
                      <p>{account?.accountId}</p>
                    </div>
                  )}
                </div>
                <div className="w-full md:w-1/3 lg:w-1/2">
                  <div className="hidden h-full items-center justify-end md:flex ">
                    <div className="relative">
                      <select
                        className="w-full cursor-pointer appearance-none rounded-lg border-none bg-transparent py-2 text-xs font-normal text-[#808080] outline-none transition hover:text-primary "
                        onChange={async (e) => {
                          await clearContext();
                          changeLocale(locales.find((locale) => locale.split('-')[1] === e.target.value));
                        }}
                        defaultValue={selectedRegion}
                      >
                        {regions?.map((region) => (
                          <option value={region} key={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="relative">
                      <select
                        className="curson-pointer w-full cursor-pointer appearance-none rounded-lg border-none bg-transparent py-2 text-xs font-normal text-[#808080] outline-none transition duration-300 hover:text-primary"
                        onChange={(e) => {
                          changeLocale(`${e.target.value}-${selectedRegion}`);
                        }}
                        defaultValue={router.locale.split('-')[0]}
                      >
                        {filteredLocales?.map((locale, index) => (
                          <option value={locale.split('-')[0]} key={index}>
                            {languageMapper[locale.split('-')[0]]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="relative flex items-center justify-between">
                <div className="w-60 max-w-full lg:w-48">
                  <ReferenceLink target={props.logoLink} className="flex h-full items-center py-0 sm:py-5 lg:py-3">
                    <div className="relative h-18 w-32 pr-3 sm:h-8 sm:w-30 sm:pr-7">
                      <Image media={props.logo} className="dark:invert" layout="fill" objectFit="contain" alt="Logo" />
                    </div>
                  </ReferenceLink>
                </div>
                <div className="flex w-full items-center justify-end md:justify-between lg:px-4">
                  <div className="flex w-full items-center justify-between">
                    <div className="w-full">
                      <nav
                        ref={menuRef}
                        className={`absolute right-4 top-full z-30 w-full max-w-[256px] justify-center rounded-lg bg-white py-5 px-6 shadow md:static md:flex md:w-full md:max-w-full md:justify-start md:py-0 md:px-0 md:shadow-none ${
                          !open && 'hidden'
                        } `}
                      >
                        <ul className="hidden items-center md:flex">
                          {Array.isArray(props.categoryHierarchy) &&
                            props.categoryHierarchy.map((category) => (
                              <ListItem
                                NavLink={'/' + category.slug}
                                menuName={category.name}
                                submenu
                                key={category.id}
                              >
                                <SubmenuGroup
                                  columns={
                                  Array.isArray(category.children)
                                    ? category.children.map((child) => ({
                                        title: child.name,
                                        href: '/' + child.slug,
                                        links: Array.isArray(child.children)
                                          ? child.children.map((subchild) => ({
                                              name: subchild.name,
                                              href: '/' + subchild.slug,
                                            }))
                                          : [],
                                      }))
                                    : []
                                  }
                                />
                              </ListItem>
                          ))}
                        </ul>
                        {/* <div className="sm:hidden">{mainButtons}</div> */}
                      </nav>
                    </div>
                  </div>
                  <div className="sm:flex">{mainButtons}</div>
                  <HamburgerMenu
                    items={props.categoryHierarchy}
                    account={account}
                    changeLocale={changeLocale}
                    clearContext={clearContext}
                    filteredLocales={filteredLocales}
                    languageMapper={languageMapper}
                    locales={locales}
                    regions={regions}
                    selectedRegion={selectedRegion}
                    bannerEnabled={props?.banner?.enabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </header>
      </FullPageWidthWrapper>
      {/* <!-- ====== Navbar Section End -->    */}
    </>
  );
};

export default ECommerceNavbar;

const TopNavItem = ({ link, name }) => {
  return (
    <li>
      <a href={link} className="text-body-color inline-block py-4 px-3 text-sm font-medium hover:text-primary">
        {name}
      </a>
    </li>
  );
};
type ListItemProps = {
  children?: React.ReactNode;
  NavLink: string;
  menuName: string;
  submenu?: boolean;
};
const ListItem = ({ children, NavLink, menuName, submenu }: ListItemProps) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const menuTrigger = useRef(null);
  const menuRef = useRef(null);

  const useClickOutside = (refs, isOpen, setIsOpen) => {
    useEffect(() => {
      const clickHandler = ({ target }) => {
        if (!refs.some((ref) => ref.current) || !isOpen || refs.some((ref) => ref.current.contains(target))) return;
        setIsOpen(false);
      };

      document.addEventListener('click', clickHandler);
      return () => document.removeEventListener('click', clickHandler);
    }, [refs, isOpen, setIsOpen]);
  };

  useClickOutside([menuTrigger], open, setOpen);

  return (
    <li className={`${submenu && 'group relative md:py-4'}`}>
      {!submenu && (
        <Link href={NavLink} locale={router.locale}>
          <a
            className="text-dark hidden justify-between py-2 text-base font-normal hover:text-primary md:mx-5 md:flex md:py-6 2xl:mx-6"
            data-testid={`category-${menuName.toLowerCase()}`}
          >
            {menuName}
          </a>
        </Link>
      )}
      {submenu && (
        <>
          <Link href={NavLink} locale={router.locale}>
            <a
              onClick={() => {
                setOpen(!open);
              }}
              className="text-dark hidden items-center justify-between py-2 text-base font-normal hover:text-primary group-hover:text-primary md:mx-6 md:flex md:py-2"
              data-testid={`category-${menuName.toLowerCase()}`}
            >
              {menuName}
              {/* <span className="pl-2">
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M5.39683 5.40046C5.20176 5.40046 5.00669 5.33284 4.84413 5.1807L0.487528 0.920728C0.373737 0.802396 0.373737 0.616445 0.471272 0.498112C0.585064 0.37978 0.76388 0.37978 0.877671 0.481208L5.23427 4.74118C5.31555 4.8257 5.46185 4.8257 5.55939 4.74118L9.91599 0.481208C10.0298 0.362875 10.2086 0.37978 10.3224 0.498112C10.4362 0.616445 10.4199 0.802396 10.3061 0.920728L5.94953 5.16379C5.78697 5.31594 5.5919 5.40046 5.39683 5.40046Z"
                    fill="currentColor"
                  ></path>
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.56447 5.4667L0.199071 1.19812C-0.0611467 0.927515 -0.0588086 0.512326 0.16261 0.243696L0.172344 0.231887L0.182952 0.220855C0.450688 -0.0575644 0.874846 -0.0571462 1.14383 0.182609L1.15072 0.188754L5.39683 4.34069L9.63276 0.198704C9.93371 -0.108224 10.3725 -0.0268578 10.6107 0.220855C10.9 0.521698 10.829 0.954046 10.5944 1.19798L10.5854 1.20743L6.22292 5.4559C5.99907 5.66539 5.70971 5.80046 5.39683 5.80046C5.10561 5.80046 4.81129 5.69783 4.5708 5.47274L4.56447 5.4667ZM9.91599 0.481208L5.55939 4.74118C5.46185 4.8257 5.31555 4.8257 5.23427 4.74118L0.877671 0.481208C0.76388 0.37978 0.585064 0.37978 0.471272 0.498112C0.373737 0.616445 0.373737 0.802396 0.487528 0.920728L4.84413 5.1807C5.00669 5.33284 5.20176 5.40046 5.39683 5.40046C5.5919 5.40046 5.78697 5.31594 5.94953 5.16379L10.3061 0.920728C10.4199 0.802396 10.4362 0.616445 10.3224 0.498112C10.2086 0.37978 10.0298 0.362875 9.91599 0.481208Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </span> */}
            </a>
          </Link>
          <div
            ref={menuRef}
            className={`relative top-full left-0 z-50 mt-6 w-fit rounded-lg bg-white px-2 transition-all group-hover:opacity-100 md:invisible md:absolute md:top-[115%] md:border-[.5px] md:border-[#e7e7e7] md:py-8 md:px-12 md:opacity-0 md:group-hover:visible md:group-hover:top-full ${
              !open ? 'hidden md:block' : 'block'
            } `}
          >
            <span className="absolute -top-[6px] left-8 -z-10 hidden h-3 w-3 rotate-45 rounded-sm border-[.5px] border-r-0 border-b-0 border-[#e7e7e7] bg-white md:block lg:left-10"></span>
            {children}
          </div>
        </>
      )}
    </li>
  );
};
type SubmenuGroupProps = {
  columns?: Array<{
    title?: string;
    href?: string;
    links: Array<{
      name?: string;
      href?: string;
    }>;
  }>;
};
const SubmenuGroup = ({ columns }: SubmenuGroupProps) => {
  const router = useRouter();

  return (
    <div className="flex flex-wrap gap-8 md:flex-nowrap md:justify-center">
      {columns?.map((col) => (
        <div className="w-full" key={col.title}>
          <div>
            {col.title !== '' && (
              <Link href={col.href} locale={router.locale}>
                <h3 className="mb-3 text-base font-semibold text-black">{col.title}</h3>
              </Link>
            )}
            {col.links?.map((link) => (
              <Link href={link.href} locale={router.locale} key={link.href}>
                <a className="block rounded py-2 text-sm font-medium text-black hover:text-primary">{link.name}</a>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CartItem = ({ image, title, desc, price }) => {
  return (
    <>
      <div className="flex items-center px-1">
        <div className="mr-3 h-10 w-full max-w-[40px] overflow-hidden rounded">
          <img src={image} alt="product image" className="w-full" />
        </div>
        <div>
          <button className="text-sm font-medium text-black hover:text-primary">{title}</button>
          <p className="text-body-color truncate text-xs font-medium">{desc}</p>
        </div>
      </div>
      <div className="px-1">
        <p className="text-base font-semibold text-black">${price}</p>
      </div>
    </>
  );
};

export const HamburgerMenu = ({
  items,
  account,
  clearContext,
  changeLocale,
  locales,
  regions,
  selectedRegion,
  languageMapper,
  filteredLocales,
  bannerEnabled,
}: {
  items: any;
  account: any;
  clearContext;
  changeLocale;
  locales: any;
  regions: any;
  selectedRegion: any;
  languageMapper: any;
  filteredLocales: any;
  bannerEnabled: boolean;
}) => {
  const [customOpen, setCustomOpen] = useState(false);
  const router = useRouter();
  const [, setLocked] = useLockedBody(false);

  const setOpen = (next: boolean) => {
    setCustomOpen(next);
    setLocked(next);
  };

  useEffect(() => {
    setOpen(false);
  }, [router]);

  return (
    <div className="z-50 w-fit text-right md:hidden">
      <Menu as="div" className="relative flex items-center justify-end">
        {({ open }) => (
          <>
            <button
              onClick={() => {
                setOpen(!customOpen);
              }}
              className="ml-4 inline-flex w-full items-center justify-end"
            >
              {customOpen ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10.7875 10.0037L19.3436 1.42662C19.5598 1.21066 19.5598 0.871275 19.3436 0.655305C19.1273 0.439336 18.7876 0.439336 18.5714 0.655305L10.0152 9.23238L1.42823 0.686158C1.21201 0.470189 0.872239 0.470189 0.656019 0.686158C0.439799 0.902127 0.439799 1.24151 0.656019 1.45748L9.24303 10.0037L0.686908 18.5808C0.470688 18.7967 0.470688 19.1361 0.686908 19.3521C0.779573 19.4446 0.934016 19.5064 1.05757 19.5064C1.18112 19.5064 1.33557 19.4446 1.42823 19.3521L10.0152 10.775L18.5714 19.3521C18.664 19.4446 18.8185 19.5064 18.942 19.5064C19.0656 19.5064 19.22 19.4446 19.3127 19.3521C19.5289 19.1361 19.5289 18.7967 19.3127 18.5808L10.7875 10.0037Z"
                    fill="#242424"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.5714 0.655305C18.7876 0.439336 19.1273 0.439336 19.3436 0.655305C19.5598 0.871275 19.5598 1.21066 19.3436 1.42662L10.7875 10.0037L19.3127 18.5808C19.5289 18.7967 19.5289 19.1361 19.3127 19.3521C19.22 19.4446 19.0656 19.5064 18.942 19.5064C18.8185 19.5064 18.664 19.4446 18.5714 19.3521L10.0152 10.775L1.42823 19.3521C1.33557 19.4446 1.18112 19.5064 1.05757 19.5064C0.934016 19.5064 0.779573 19.4446 0.686908 19.3521C0.470688 19.1361 0.470688 18.7967 0.686908 18.5808L9.24303 10.0037L0.656019 1.45748C0.439799 1.24151 0.439799 0.902127 0.656019 0.686158C0.872239 0.470189 1.21201 0.470189 1.42823 0.686158L10.0152 9.23238L18.5714 0.655305ZM10.014 8.53426L18.2213 0.306877C18.6305 -0.101872 19.2839 -0.102502 19.6931 0.306247C20.1021 0.714787 20.1023 1.36627 19.6937 1.77506M19.6937 1.77506L11.4845 10.0043L19.6622 18.2317C19.662 18.2315 19.6624 18.2319 19.6622 18.2317C20.0709 18.6405 20.0712 19.2926 19.6622 19.7012C19.4758 19.8873 19.1952 20 18.9421 20C18.6889 20 18.4083 19.8873 18.2219 19.7012L10.0147 11.4738L1.77774 19.7012C1.59134 19.8873 1.31075 20 1.05762 20C0.804484 20 0.523892 19.8873 0.337491 19.7012C-0.0717324 19.2924 -0.0717324 18.6405 0.337491 18.2317L8.54415 10.005L0.307232 1.80717C-0.10199 1.39842 -0.102621 0.74585 0.306602 0.3371C0.715695 -0.0715195 1.3681 -0.0716494 1.77735 0.336711C1.77722 0.336581 1.77748 0.33684 1.77735 0.336711L10.014 8.53426"
                    fill="#242424"
                  />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3.46973 3.98389H20.5299C20.8955 3.98389 21.2306 3.5491 21.2306 2.98389C21.2306 2.41867 20.9259 1.98389 20.5299 1.98389H3.46973C3.10415 1.98389 2.76904 2.41867 2.76904 2.98389C2.76904 3.5491 3.10415 3.98389 3.46973 3.98389Z"
                    fill="#242424"
                  />
                  <path
                    d="M20.53 10.9839H11.7866C11.421 10.9839 11.0859 11.4187 11.0859 11.9839C11.0859 12.5056 11.3906 12.9839 11.7866 12.9839H20.53C20.8955 12.9839 21.2306 12.5491 21.2306 11.9839C21.2306 11.4187 20.8955 10.9839 20.53 10.9839Z"
                    fill="#242424"
                  />
                  <path
                    d="M20.5299 19.9839H3.46973C3.10415 19.9839 2.76904 20.4187 2.76904 20.9839C2.76904 21.5491 3.07369 21.9839 3.46973 21.9839H20.5299C20.8955 21.9839 21.2306 21.5491 21.2306 20.9839C21.2306 20.4187 20.8955 19.9839 20.5299 19.9839Z"
                    fill="#242424"
                  />
                </svg>
              )}
            </button>

            {customOpen ? (
              <Menu.Items
                static
                className={`fixed inset-[0] ${
                  bannerEnabled ? 'top-[104px]' : 'top-[72px]'
                } z-50 w-screen origin-top-right divide-y divide-gray-100 overflow-y-scroll bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none`}
              >
                <div className="flex h-16 w-full items-center justify-between bg-[#102B33] px-4 sm:px-10">
                  <div className="flex gap-6">
                    <select
                      className="appearance-black-200 w-fit rounded-[2px] border-none bg-[#F6F9FF] py-2 text-sm font-medium text-primary outline-black-200 transition"
                      onChange={async (e) => {
                        await clearContext();
                        changeLocale(locales.find((locale) => locale.split('-')[1] === e.target.value));
                      }}
                      defaultValue={selectedRegion}
                    >
                      {regions?.map((region) => (
                        <option value={region} key={region}>
                          {region}
                        </option>
                      ))}
                    </select>

                    <select
                      className="appearance-black-200 w-fit rounded-[2px] border-none bg-[#F6F9FF] py-2 text-sm font-medium text-primary outline-black-200 transition"
                      onChange={(e) => {
                        changeLocale(`${e.target.value}-${selectedRegion}`);
                      }}
                      defaultValue={router.locale.split('-')[0]}
                    >
                      {filteredLocales?.map((locale, index) => (
                        <option value={locale.split('-')[0]} key={index}>
                          {languageMapper[locale.split('-')[0]]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-6">
                    <div
                      className="relative flex h-10 w-10 items-center justify-center rounded-full border-[.5px] border-[#e7e7e7] bg-[#f4f7ff]"
                      onClick={() => setCustomOpen(!customOpen)}
                    >
                      <ReferenceLink
                        target={{
                          link: '/search',
                          type: 'link',
                          openInNewWindow: false,
                        }}
                        className="text-dark relative flex h-10 w-10 items-center justify-center"
                        data-testid="search-trigger"
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M2.79956 6.81696C2.79956 3.60434 5.37675 1 8.55587 1C11.735 1 14.3122 3.60434 14.3122 6.81696C14.3122 10.0296 11.735 12.6339 8.55587 12.6339C5.37675 12.6339 2.79956 10.0296 2.79956 6.81696ZM8.55587 2.30069C6.08761 2.30069 4.08669 4.32269 4.08669 6.81696C4.08669 9.31122 6.08761 11.3332 8.55587 11.3332C11.0241 11.3332 13.0251 9.31122 13.0251 6.81696C13.0251 4.32269 11.0241 2.30069 8.55587 2.30069Z"
                            fill="#242424"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M11.7893 10.9197C12.0712 10.701 12.4752 10.7545 12.6917 11.0394L15.6664 14.9535C15.8829 15.2384 15.8298 15.6466 15.548 15.8654C15.2661 16.0842 14.8621 16.0306 14.6456 15.7457L11.6709 11.8316C11.4544 11.5468 11.5074 11.1385 11.7893 10.9197Z"
                            fill="#242424"
                          />
                        </svg>
                      </ReferenceLink>
                    </div>
                    <div
                      className="relative flex h-10 w-10 items-center justify-center rounded-full border-[.5px] border-[#e7e7e7] bg-[#f4f7ff]"
                      onClick={() => setCustomOpen(!customOpen)}
                    >
                      <AccountButton
                        account={account}
                        accountLink={{
                          link: !router.asPath.includes('current') ? `/login?current${router.asPath}` : router.asPath,
                          target: !router.asPath.includes('current') ? `/login?current${router.asPath}` : router.asPath,
                          type: 'link',
                          openInNewWindow: false,
                        }}
                        data-testid="account-trigger"
                      />
                    </div>
                  </div>
                </div>
                <div className="px-1 py-1">
                  {items?.map((category, index) => (
                    <Menu.Item key={index}>
                      <Disclosure>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="flex w-full items-center justify-between px-4 pt-4 text-left text-base font-bold text-[#242424] sm:px-10">
                              <div className="flex w-full items-center justify-between border-b border-[#E7E7E7] pb-4">
                                <Link href={'/' + category.slug}>
                                  <a onClick={() => setOpen(!customOpen)}>{category.name}</a>
                                </Link>
                                <DisclosureArrows open={open} />
                              </div>
                            </Disclosure.Button>
                            <Disclosure.Panel className="pl-4 pb-4 pt-4 text-sm text-gray-500">
                              {category.children?.map((child, idx) => (
                                <Disclosure key={idx}>
                                  {({ open: openLast }) => (
                                    <>
                                      <Disclosure.Button className="flex w-full items-center justify-between px-4 py-2 text-left text-base font-normal text-[#242424] sm:px-10">
                                        <div
                                          className={`flex w-full items-center justify-between ${
                                            !openLast && 'border-b border-[#E7E7E7]'
                                          } pb-4`}
                                        >
                                          <Link href={'/' + child.slug}>
                                            <a onClick={() => setOpen(!customOpen)}>{child.name}</a>
                                          </Link>
                                          <DisclosureArrows open={openLast} />
                                        </div>
                                      </Disclosure.Button>
                                      <Disclosure.Panel>
                                        <div className="pl-4 pb-4">
                                          {child.children?.map((subchild, i) => (
                                            <Disclosure key={i}>
                                              <Disclosure.Button className="flex w-full items-center justify-between pl-4 pb-3 text-left text-base font-medium text-[#808080] sm:px-10">
                                                <Link href={'/' + subchild.slug}>
                                                  <a onClick={() => setOpen(!customOpen)}>{subchild.name}</a>
                                                </Link>
                                              </Disclosure.Button>
                                            </Disclosure>
                                          ))}
                                        </div>
                                      </Disclosure.Panel>
                                    </>
                                  )}
                                </Disclosure>
                              ))}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            ) : (
              <></>
            )}
          </>
        )}
      </Menu>
    </div>
  );
};

const DisclosureArrows = ({ open }: { open: boolean }) => {
  return (
    <div className={open ? 'rotate-180 transition duration-300' : 'rotate-0 transition duration-300'}>
      <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M5.5 6.46764C5.36944 6.46764 5.26063 6.42412 5.15183 6.33707L0.146884 1.41917C-0.0489614 1.22333 -0.0489614 0.918676 0.146884 0.722831C0.34273 0.526985 0.647379 0.526985 0.843225 0.722831L5.5 5.2708L10.1568 0.679309C10.3526 0.483463 10.6573 0.483463 10.8531 0.679309C11.049 0.875155 11.049 1.1798 10.8531 1.37565L5.84817 6.29355C5.73937 6.40236 5.63056 6.46764 5.5 6.46764Z"
          fill="#242424"
        />
      </svg>
    </div>
  );
};
